import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import PageShell from "@/components/PageShell";
import { EnquiryForm } from "@/components/EnquiryForm";
import { usePageContent } from "@/hooks/usePageContent";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import { useSEO } from "@/hooks/useSEO";
import axios from "axios";

const API = getApiUrl();
const AGENT_IMAGE = "https://images.unsplash.com/photo-1632131016411-0183bc4efdd8?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function ContactPage() {
  const { get } = usePageContent("contact");
  const { styles } = useSiteStyles();
  const [seoData, setSeoData] = useState(null);
  const [searchParams] = useSearchParams();

  // Property details can be piped through from a property-page CTA so the
  // enquiry form arrives pre-scoped to a specific listing.
  const propertyId    = searchParams.get("property_id") || undefined;
  const propertyTitle = searchParams.get("property_title") || undefined;
  const agentId       = searchParams.get("agent_id") || undefined;
  const agentName     = searchParams.get("agent_name") || undefined;

  useEffect(() => {
    fetchSeoData();
  }, []);

  const fetchSeoData = async () => {
    try {
      const response = await axios.get(`${API}/seo/contact`);
      setSeoData(response.data);
    } catch (error) {
      console.error("Error fetching SEO data:", error);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      value: styles.contactEmail || "info@selllocalhomes.co.uk",
      link: `mailto:${styles.contactEmail || "info@selllocalhomes.co.uk"}`,
    },
    {
      icon: Phone,
      title: "Call Us",
      value: styles.contactPhone || "",
      link: styles.contactPhone ? `tel:${styles.contactPhone.replace(/[^0-9+]/g, "")}` : null,
    },
    {
      icon: MapPin,
      title: "Visit Us",
      value: styles.companyAddress || "",
      link: null,
    },
    {
      icon: Clock,
      title: "Office Hours",
      value: "Mon - Fri: 9:00 AM - 6:00 PM",
      link: null,
    },
  ];

  // Apply SEO tags via direct DOM manipulation
  useSEO(
    seoData ? {
      seo_title: seoData.seo_title || "Contact Us | SellLocalHomes",
      seo_description: seoData.seo_description || "Contact SellLocalHomes to enquire about a property or get help with your property search.",
      seo_keywords: seoData.seo_keywords || "contact, property enquiry, real estate contact",
      seo_robots: seoData.seo_robots || "index, follow",
      canonical_url: "https://selllocalhomes.co.uk/contact"
    } : {
      seo_title: "Contact Us | SellLocalHomes",
      seo_description: "Contact SellLocalHomes to enquire about a property or get help with your property search.",
      seo_keywords: "contact, property enquiry, real estate contact",
      seo_robots: "index, follow",
      canonical_url: "https://selllocalhomes.co.uk/contact"
    },
    { og_type: "website" }
  );

  return (
    <PageShell>
      <div className="min-h-screen" data-testid="contact-page">
      {/* Page Header */}
      <section className="pt-8 pb-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="space-y-4 animate-fade-in-up">
            <span className="text-sm tracking-widest uppercase font-semibold text-muted-foreground">
              {get("page_subtitle", "Get In Touch")}
            </span>
            <h1 className="font-serif text-4xl md:text-6xl font-medium">
              {get("page_title", "Contact Us")}
            </h1>
            <div className="section-divider" />
            <p className="text-lg text-muted-foreground max-w-xl">
              {get("page_description", "Have questions about a property? Ready to find your dream home? We're here to help.")}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: Contact Info & Image */}
            <div className="space-y-12 animate-fade-in-up">
              {/* Agent Image */}
              <div className="relative overflow-hidden image-hover aspect-[4/3]">
                <img
                  src={AGENT_IMAGE}
                  alt="Our Team"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                  <h3 className="font-serif text-2xl text-white">Our Expert Team</h3>
                  <p className="text-white/80">Ready to assist you</p>
                </div>
              </div>

              {/* Contact Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {contactInfo.map((item, index) => (
                  <div
                    key={index}
                    className="p-6 bg-muted/30 border border-border/50 hover:bg-white transition-colors duration-300"
                    data-testid={`contact-info-${item.title.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-6 h-6 text-primary mb-4" />
                    <h4 className="font-serif text-lg font-medium mb-2">{item.title}</h4>
                    {item.link ? (
                      <a
                        href={item.link}
                        className="text-muted-foreground hover:text-primary link-underline inline-block"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-muted-foreground text-sm">{item.value}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="animate-fade-in-up animation-delay-200">
              <div className="glass-card p-8 lg:p-10">
                <div className="space-y-6 mb-8">
                  <h2 className="font-serif text-3xl font-medium">
                    {propertyTitle
                      ? `Enquire About ${propertyTitle}`
                      : get("form_title", "Send Us a Message")}
                  </h2>
                  <p className="text-muted-foreground">
                    {propertyTitle
                      ? "Send us your details and we'll be in touch about this property shortly."
                      : get("form_description", "Fill out the form below and we'll get back to you as soon as possible.")}
                  </p>
                  <div className="section-divider" />
                </div>
                <EnquiryForm
                  propertyId={propertyId}
                  propertyTitle={propertyTitle}
                  agentId={agentId}
                  agentName={agentName}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section - Placeholder */}
      <section className="h-96 bg-muted/50 relative" data-testid="map-section">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-primary mx-auto mb-4" />
            <h3 className="font-serif text-2xl font-medium">
              {(styles.companyAddress || "123 Real Estate Ave").split(",")[0]}
            </h3>
            <p className="text-muted-foreground">
              {(styles.companyAddress || "123 Real Estate Ave, City").split(",").slice(1).join(",")}
            </p>
          </div>
        </div>
      </section>

    </div>
    </PageShell>
  );
}
